import type { PluginConnection, PluginToolkit } from "@ark-bot/protocol";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../api.js";
import { shell } from "../host.js";
import { useText } from "../i18n.js";
import { anyPending, appendPage, connectedToolkits, connectionsByToolkit, orderToolkits, rowState, type PluginRowState } from "../plugins.js";
import { PLUGINS } from "../text/plugins.js";

interface Props {
  readonly onClose: () => void;
}

const SEARCH_DEBOUNCE_MS = 250;
/** How often the panel asks again while a sign-in is open in another tab. */
const PENDING_POLL_MS = 2_500;

const messageOf = (failure: unknown): string => (failure instanceof Error ? failure.message : String(failure));

/** The app's own logo on a light tile, since most are drawn for a white page; its initial when there is none. */
function PluginLogo({ toolkit }: { readonly toolkit: PluginToolkit }): React.JSX.Element {
  const [failed, setFailed] = useState(false);
  if (toolkit.logo.length === 0 || failed) {
    return (
      <span className="plugin-logo plugin-logo-letter" aria-hidden="true">
        {toolkit.name.charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <span className="plugin-logo" aria-hidden="true">
      <img src={toolkit.logo} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    </span>
  );
}

/**
 * Plugins (ARCHITECTURE §6.18): every app Composio can connect, searchable,
 * each with a button that connects it. Connecting opens the app's own sign-in
 * in a new tab; the panel waits for it to land and says so. What is connected
 * here is what every agent's app tools can use.
 */
export function Plugins({ onClose }: Props): React.JSX.Element {
  const t = useText(PLUGINS);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<"all" | "connected">("all");
  const [items, setItems] = useState<PluginToolkit[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState<PluginConnection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Readonly<Record<string, string>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  /** The sign-in link each app was last given, and whether a window took it; to open again, or at all when the browser blocked it. */
  const [links, setLinks] = useState<Readonly<Record<string, { readonly url: string; readonly opened: boolean }>>>({});
  const [now, setNow] = useState(() => Date.now());
  /** Every app the catalogue has shown, so the Connected tab can draw one found on an earlier page. */
  const known = useRef(new Map<string, PluginToolkit>());
  const request = useRef(0);

  const refreshConnections = useCallback(async (): Promise<void> => {
    try {
      const list = await api.pluginConnections();
      setConnections(list.connections);
      setNow(Date.now());
    } catch (failure) {
      setError(messageOf(failure));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .pluginsStatus()
      .then((status) => {
        if (cancelled) return;
        setConfigured(status.configured);
        if (status.configured) void refreshConnections();
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        // An older box without the routes answers 404: the same as having none set up.
        setConfigured(false);
        if (!/not found|404/i.test(messageOf(failure))) setError(messageOf(failure));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshConnections]);

  const load = useCallback(async (search: string, cursor: string | null): Promise<void> => {
    const ticket = ++request.current;
    setLoading(true);
    setError(null);
    try {
      const page = await api.pluginToolkits(search, cursor);
      if (ticket !== request.current) return;
      for (const toolkit of page.items) known.current.set(toolkit.slug, toolkit);
      setItems((shown) => (cursor == null ? page.items : appendPage(shown, page.items)));
      setNextCursor(page.nextCursor);
      setTotal(page.total);
    } catch (failure) {
      if (ticket === request.current) setError(messageOf(failure));
    } finally {
      if (ticket === request.current) setLoading(false);
    }
  }, []);

  // The catalogue follows the search box, a beat behind the typing.
  useEffect(() => {
    if (configured !== true) return;
    const timer = setTimeout(() => void load(query, null), query.length === 0 ? 0 : SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [configured, query, load]);

  // A sign-in happens in another tab: ask again while one is open, and whenever the user comes back to this one.
  const waiting = anyPending(connections, now);
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => void refreshConnections(), PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [waiting, refreshConnections]);
  useEffect(() => {
    if (configured !== true) return;
    const back = (): void => {
      if (document.visibilityState === "visible") void refreshConnections();
    };
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", back);
    return () => {
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", back);
    };
  }, [configured, refreshConnections]);

  const byToolkit = useMemo(() => connectionsByToolkit(connections, now), [connections, now]);
  const rows = useMemo(() => {
    if (tab === "connected") {
      const needle = query.trim().toLowerCase();
      return connectedToolkits(byToolkit, known.current).filter((toolkit) => needle.length === 0 || `${toolkit.slug} ${toolkit.name}`.toLowerCase().includes(needle));
    }
    return query.trim().length === 0 ? orderToolkits(items, byToolkit) : items;
  }, [tab, query, items, byToolkit]);

  const noteRowError = (slug: string, message: string | null): void =>
    setRowErrors((errors) => {
      const next = { ...errors };
      if (message == null) delete next[slug];
      else next[slug] = message;
      return next;
    });

  const connect = async (toolkit: PluginToolkit): Promise<void> => {
    if (busy != null) return;
    setBusy(toolkit.slug);
    noteRowError(toolkit.slug, null);
    // Opened now, while the click still counts as the user's own: a window
    // opened after the request comes back is a popup, and browsers block it.
    // The packaged app hands a link to the phone's browser instead.
    let popup: Window | null = null;
    if (!shell()) {
      popup = window.open("", "_blank");
      try {
        if (popup != null) {
          popup.document.title = t.popupTitle(toolkit.name);
          popup.document.body.style.cssText = "font:15px system-ui;background:#171717;color:#ececec;display:grid;place-items:center;height:100vh;margin:0";
          popup.document.body.textContent = t.popupBody(toolkit.name);
        }
      } catch {
        // Only the courtesy text; the window still works.
      }
    }
    try {
      const link = await api.connectPlugin(toolkit.slug);
      let opened = false;
      if (popup != null && !popup.closed) {
        popup.opener = null;
        popup.location.replace(link.url);
        opened = true;
      } else if (shell()) {
        opened = window.open(link.url, "_blank") != null;
      }
      setLinks((current) => ({ ...current, [toolkit.slug]: { url: link.url, opened } }));
      await refreshConnections();
    } catch (failure) {
      popup?.close();
      noteRowError(toolkit.slug, messageOf(failure));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (toolkit: PluginToolkit, connection: PluginConnection): Promise<void> => {
    if (busy != null) return;
    setBusy(toolkit.slug);
    setConfirming(null);
    noteRowError(toolkit.slug, null);
    try {
      await api.disconnectPlugin(connection.id);
      await refreshConnections();
    } catch (failure) {
      noteRowError(toolkit.slug, messageOf(failure));
    } finally {
      setBusy(null);
    }
  };

  const actions = (toolkit: PluginToolkit, state: PluginRowState): React.JSX.Element => {
    const working = busy === toolkit.slug;
    switch (state.kind) {
      case "no-auth":
        return <span className="plugin-note muted">{t.noSignIn}</span>;
      case "connect":
        return (
          <button type="button" className="primary" disabled={busy != null} onClick={() => void connect(toolkit)}>
            {working ? t.connecting : t.connect}
          </button>
        );
      case "pending": {
        const link = links[toolkit.slug];
        return (
          <>
            <span className="plugin-note muted">{t.waitingSignIn}</span>
            {link != null ? (
              <a className={`plugin-reopen${link.opened ? " linkish" : " plugin-open-link"}`} href={link.url} target="_blank" rel="noopener noreferrer">
                {link.opened ? t.openAgain : t.openSignIn}
              </a>
            ) : null}
            <button type="button" className="quiet" disabled={busy != null} onClick={() => void disconnect(toolkit, state.connection)}>
              {t.cancel}
            </button>
          </>
        );
      }
      case "broken":
        return (
          <button type="button" className="primary" disabled={busy != null} onClick={() => void connect(toolkit)} title={t.composioReports(state.connection.status)}>
            {working ? t.connecting : t.reconnect}
          </button>
        );
      case "active":
        return confirming === toolkit.slug ? (
          <>
            <button type="button" className="danger" disabled={busy != null} onClick={() => void disconnect(toolkit, state.connection)}>
              {working ? t.disconnecting : t.disconnect}
            </button>
            <button type="button" className="quiet" onClick={() => setConfirming(null)}>
              {t.keep}
            </button>
          </>
        ) : (
          <button type="button" className="quiet" disabled={busy != null} onClick={() => setConfirming(toolkit.slug)}>
            {t.disconnect}
          </button>
        );
    }
  };

  const badge = (state: PluginRowState): React.JSX.Element | null => {
    if (state.kind === "active") return <span className="plugin-badge ok">{t.connected}</span>;
    if (state.kind === "broken") return <span className="plugin-badge warn">{t.needsReconnecting}</span>;
    return null;
  };

  const connectedCount = [...byToolkit.values()].filter((connection) => connection.state === "active").length;

  return (
    <div className="plugins">
      <div className="settings-head plugins-head">
        <h2>{t.title}</h2>
        <span className="muted">{t.about}</span>
        <button type="button" className="head-close" aria-label={t.close} onClick={onClose}>
          ×
        </button>
      </div>
      {configured === false ? (
        <div className="plugins-empty">
          <p>{t.notSetUp}</p>
          <p className="muted">
            {t.notSetUpBefore}
            <code>ARK_BOT_COMPOSIO_API_KEY</code>
            {t.notSetUpAfter}
          </p>
        </div>
      ) : (
        <>
          <div className="plugins-toolbar">
            <label className="search plugins-search">
              <span className="search-icon" aria-hidden="true">
                ⌕
              </span>
              <input type="search" value={query} placeholder={tab === "all" ? t.searchAll : t.searchConnected} aria-label={t.searchLabel} autoFocus onChange={(event) => setQuery(event.target.value)} />
            </label>
            <div className="plugins-tabs" role="group" aria-label={t.show}>
              <button type="button" className="quiet" aria-pressed={tab === "all"} onClick={() => setTab("all")}>
                {t.allApps}
              </button>
              <button type="button" className="quiet" aria-pressed={tab === "connected"} onClick={() => setTab("connected")}>
                {t.connected}
                {connectedCount > 0 ? <span className="plugins-count">{connectedCount}</span> : null}
              </button>
            </div>
          </div>
          {error != null ? <div className="settings-error plugins-error">{error}</div> : null}
          <ul className="plugin-list" aria-busy={loading || configured == null}>
            {rows.map((toolkit) => {
              const state = rowState(toolkit, byToolkit);
              const rowError = rowErrors[toolkit.slug];
              return (
                <li key={toolkit.slug} className={`plugin-row plugin-${state.kind}`}>
                  <PluginLogo toolkit={toolkit} />
                  <div className="plugin-text">
                    <div className="plugin-name">
                      <span>{toolkit.name}</span>
                      {badge(state)}
                    </div>
                    {toolkit.description.length > 0 ? <div className="plugin-description muted">{toolkit.description}</div> : null}
                    {rowError != null ? <div className="plugin-row-error">{rowError}</div> : null}
                  </div>
                  <div className="plugin-actions">{actions(toolkit, state)}</div>
                </li>
              );
            })}
          </ul>
          {rows.length === 0 && !loading && configured === true ? (
            <div className="plugins-empty muted">{tab === "connected" ? (query.trim().length > 0 ? t.noneMatch : t.nothingConnected) : t.noAppMatches(query.trim())}</div>
          ) : null}
          {loading && items.length === 0 && tab === "all" ? <div className="plugins-empty muted">{t.loadingApps}</div> : null}
          {tab === "all" && nextCursor != null ? (
            <div className="plugins-more">
              <button type="button" className="quiet" disabled={loading} onClick={() => void load(query, nextCursor)}>
                {loading ? t.loading : total > items.length ? t.showMoreOf(items.length, total) : t.showMore}
              </button>
            </div>
          ) : null}
          <div className="plugins-foot muted">{t.foot}</div>
        </>
      )}
    </div>
  );
}
